import { classNames } from "@/shared/utils/classnames";
import i18n from "@/app/i18n";
import React, { FC, Fragment, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCcMastercard,
  faCcStripe,
  faCcVisa,
} from "@fortawesome/free-brands-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  faCreditCard,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { PaymentMethod } from "@/app/billing/payment-method";

export type PaymentMethodCardProps = {
  isLoading?: boolean;
  isDefault?: boolean;
  paymentMethod?: PaymentMethod;
  onDelete?: (id: string) => void;
  onSet?: (id: string) => void;
};

export const PaymentMethodCard: FC<PaymentMethodCardProps> = ({
  isLoading,
  paymentMethod,
  onDelete,
  onSet,
}) => {
  const [t] = useTranslation();

  const handleDelete = useCallback(() => {
    onDelete(paymentMethod.id);
  }, [paymentMethod, onDelete]);

  const handleSet = useCallback(() => {
    onSet(paymentMethod.id);
  }, [paymentMethod, onSet]);

  return (
    <li
      className={classNames(
        "col-span-1 flex rounded-md shadow-sm ",
        paymentMethod?.default ? "ring-2 ring-primary" : ""
      )}
    >
      <div className="bg-primary flex w-16 flex-shrink-0 items-center justify-center rounded-l-md text-sm font-medium text-white">
        {isLoading && (
          <Fragment>
            <FontAwesomeIcon icon={faCreditCard} className="text-2xl" />
          </Fragment>
        )}
        {paymentMethod?.type == "card" && (
          <Fragment>
            {paymentMethod?.card.brand == "visa" && (
              <FontAwesomeIcon icon={faCcVisa} className="text-2xl" />
            )}
            {paymentMethod?.card.brand == "mastercard" && (
              <FontAwesomeIcon icon={faCcMastercard} className="text-2xl" />
            )}
          </Fragment>
        )}
        {paymentMethod?.type == "sepa_debit" && (
          <Fragment>
            <FontAwesomeIcon icon={faCcStripe} className="text-2xl" />
          </Fragment>
        )}
      </div>
      <div className="flex flex-1 items-center justify-between truncate rounded-r-md border-b border-r border-t border-gray-200 bg-white">
        <div className="flex-1 truncate px-4 py-2 text-sm">
          {isLoading ? (
            <a className="animate-pulse bg-gray-300 w-full flex-1 flex h-4 rounded-sm mb-2"></a>
          ) : (
            <a
              className={classNames(
                "font-medium text-gray-900 hover:text-gray-600"
              )}
            >
              {paymentMethod?.type == "card"
                ? `**** **** **** ${paymentMethod?.card.last4}`
                : ""}
              {` - ${paymentMethod?.card.expirationMonth}/${paymentMethod?.card.expirationYear}`}
            </a>
          )}
          <div className="flex gap-4">
            {isLoading ? (
              <a className="animate-pulse bg-gray-300 w-2/4 flex h-4 rounded-sm"></a>
            ) : (
              <p className="text-gray-500">
                {DateTime.fromISO(paymentMethod.createdAt)
                  .setLocale(i18n.language)
                  .toFormat("d MMMM yyyy")}
              </p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 pr-2">
          <Menu>
            <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
              <span className="sr-only"></span>
              <FontAwesomeIcon
                icon={faEllipsisVertical}
                aria-hidden="true"
                className="h-5 w-5"
              />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <MenuItem disabled={paymentMethod?.default}>
                <a
                  onClick={handleSet}
                  className={`block px-4 py-2 text-sm data-[focus]:bg-gray-100 data-[focus]:text-gray-900 ${paymentMethod?.default ? "text-gray-500" : "text-primary-500"} `}
                  role="button"
                >
                  Seleccionar
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  onClick={handleDelete}
                  className="block px-4 py-2 text-sm text-red-500 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                  role="button"
                >
                  Eliminar
                </a>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </li>
  );
};
